import { useEffect, useState, useCallback } from 'react';
import { Lock, KeyRound, Check, AlertCircle, Loader2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('casa_admin_token')}`,
  'Content-Type': 'application/json',
});

/**
 * Casa de Candy — encrypted credentials manager.
 *
 * Per Pass 3 spec:
 *   - Secrets are never returned by the API once saved (masked previews only).
 *   - Per-service enable toggle + test button + audit row.
 *   - Empty input on PUT means "leave unchanged" so admins can edit one
 *     field at a time without re-typing every secret.
 */
export function CredentialsManager() {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [testingKey, setTestingKey] = useState(null);
  const [toggleKey, setToggleKey] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '', service: '' });

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/credentials`, { headers: authHeaders() });
      if (res.ok) {
        const j = await res.json();
        setData(j);
        // Reset drafts whenever we reload so masked previews don't get
        // confused with what the admin is typing in.
        const fresh = {};
        for (const item of j.items || []) {
          fresh[item.key] = {};
        }
        setDrafts(fresh);
      }
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setDraft = (svc, field, value) => {
    setDrafts({
      ...drafts,
      [svc]: { ...(drafts[svc] || {}), [field]: value },
    });
  };

  const save = async (svc) => {
    const draft = drafts[svc] || {};
    const fields = {};
    for (const [k, v] of Object.entries(draft)) {
      if (v === undefined || v === null) continue;
      fields[k] = { value: v };
    }
    setSavingKey(svc); setMsg({ type: '', text: '', service: svc });
    try {
      const res = await fetch(`${API_URL}/api/admin/credentials/${svc}`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ fields }),
      });
      if (res.ok) {
        setMsg({ type: 'ok', text: 'Saved (encrypted at rest).', service: svc });
        await load();
      } else {
        const j = await res.json().catch(() => ({}));
        setMsg({ type: 'error', text: j.detail || 'Save failed', service: svc });
      }
    } catch (e) {
      setMsg({ type: 'error', text: String(e), service: svc });
    } finally {
      setSavingKey(null);
    }
  };

  const toggle = async (svc, enabled) => {
    setToggleKey(svc); setMsg({ type: '', text: '', service: svc });
    try {
      const res = await fetch(`${API_URL}/api/admin/credentials/${svc}/toggle`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ enabled }),
      });
      if (res.ok) await load();
    } finally {
      setToggleKey(null);
    }
  };

  const runTest = async (svc) => {
    setTestingKey(svc); setMsg({ type: '', text: '', service: svc });
    try {
      const res = await fetch(`${API_URL}/api/admin/credentials/${svc}/test`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({}),
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j.status === 'ok') {
        setMsg({ type: 'ok', text: 'Test succeeded.', service: svc });
      } else {
        setMsg({
          type: 'error',
          service: svc,
          text: j.error || j.detail || 'Test failed',
        });
      }
      await load();
    } finally {
      setTestingKey(null);
    }
  };

  const clearField = async (svc, fieldKey) => {
    setSavingKey(svc); setMsg({ type: '', text: '', service: svc });
    try {
      const res = await fetch(`${API_URL}/api/admin/credentials/${svc}`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ fields: { [fieldKey]: { clear: true } } }),
      });
      if (res.ok) await load();
    } finally {
      setSavingKey(null);
    }
  };

  if (!data) {
    return (
      <div className="text-sm text-[#A7A3BA] flex items-center gap-2" data-testid="credentials-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading credentials…
      </div>
    );
  }

  const enc = data.encryption || {};

  return (
    <div className="space-y-6" data-testid="credentials-manager">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-lg font-semibold text-[#F4F2FF] flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#27D7FF]" />
            Encrypted Service Credentials
          </h3>
          <p className="text-xs text-[#A7A3BA] mt-1 max-w-2xl">
            Provider keys are encrypted at rest using AES-256-GCM. Plaintext
            values are never returned to the admin UI after save — fields
            display a masked preview like ••••••••1234.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-xs text-[#A7A3BA] hover:text-[#F4F2FF] inline-flex items-center gap-1"
          data-testid="credentials-refresh"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      <div
        className={`px-3 py-2 rounded-md text-xs inline-flex items-center gap-2 ${
          enc.configured
            ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
            : 'bg-red-500/10 text-red-300 border border-red-500/30'
        }`}
        data-testid="credentials-encryption-status"
      >
        <KeyRound className="w-3 h-3" />
        {enc.configured
          ? `Encryption active · ${enc.algorithm}`
          : `Encryption disabled — ${enc.error || 'master key missing'}`}
      </div>

      {(data.items || []).map((item) => {
        const tested = item.last_test_status;
        return (
          <div
            key={item.key}
            className="rounded-xl border border-white/5 bg-[#0F0E1A] p-5"
            data-testid={`credentials-card-${item.key}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
              <div>
                <h4 className="font-medium text-[#F4F2FF]">{item.label}</h4>
                <p className="text-xs text-[#A7A3BA] mt-0.5 max-w-xl">{item.description}</p>
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-[#A7A3BA] select-none cursor-pointer">
                <span>{item.enabled ? 'Enabled' : 'Disabled'}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={item.enabled}
                  disabled={toggleKey === item.key}
                  onClick={() => toggle(item.key, !item.enabled)}
                  className={`relative w-10 h-6 rounded-full transition-colors ${
                    item.enabled ? 'bg-[#FF4DA6]' : 'bg-white/10'
                  }`}
                  data-testid={`credentials-toggle-${item.key}`}
                >
                  <span
                    className={`absolute top-0.5 ${item.enabled ? 'right-0.5' : 'left-0.5'} w-5 h-5 rounded-full bg-white transition-all`}
                  />
                </button>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              {item.fields.map((f) => {
                const draft = drafts[item.key] || {};
                const display = f.secret
                  ? (draft[f.key] !== undefined ? draft[f.key] : '')
                  : (draft[f.key] !== undefined ? draft[f.key] : (f.value || ''));
                return (
                  <div key={f.key} className="flex flex-col gap-1">
                    <label className="text-[11px] text-[#A7A3BA] uppercase tracking-wider">
                      {f.label}{f.required ? ' *' : ''}
                    </label>
                    {f.secret && f.has_value && (
                      <p className="text-[11px] text-[#FFB6D8] font-mono" data-testid={`credentials-masked-${item.key}-${f.key}`}>
                        Stored: {f.masked || '(set)'}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <input
                        type={f.secret ? 'password' : 'text'}
                        value={display}
                        onChange={(e) => setDraft(item.key, f.key, e.target.value)}
                        placeholder={f.secret && f.has_value ? 'Leave blank to keep current' : (f.placeholder || '')}
                        className="input-neon flex-1"
                        autoComplete="off"
                        data-testid={`credentials-input-${item.key}-${f.key}`}
                      />
                      {f.secret && f.has_value && (
                        <button
                          type="button"
                          onClick={() => clearField(item.key, f.key)}
                          className="text-[11px] text-[#A7A3BA] hover:text-red-300 px-2"
                          title="Clear this field"
                          data-testid={`credentials-clear-${item.key}-${f.key}`}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-white/5">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => save(item.key)}
                  disabled={savingKey === item.key}
                  className="btn-neon btn-neon-pink text-xs px-4 py-2"
                  data-testid={`credentials-save-${item.key}`}
                >
                  {savingKey === item.key ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => runTest(item.key)}
                  disabled={testingKey === item.key}
                  className="text-xs px-4 py-2 rounded-md border border-white/10 text-[#F4F2FF] hover:bg-white/5 disabled:opacity-50"
                  data-testid={`credentials-test-${item.key}`}
                >
                  {testingKey === item.key ? 'Testing…' : 'Run test'}
                </button>
              </div>
              <div className="text-[11px] text-[#A7A3BA] flex flex-col items-end">
                {tested === 'ok' && (
                  <span className="text-emerald-300 inline-flex items-center gap-1" data-testid={`credentials-last-test-${item.key}`}>
                    <Check className="w-3 h-3" />
                    Last test passed · {item.last_tested_at?.slice(0, 16).replace('T', ' ')}
                  </span>
                )}
                {tested === 'error' && (
                  <span className="text-red-300 inline-flex items-center gap-1" data-testid={`credentials-last-test-${item.key}`}>
                    <AlertCircle className="w-3 h-3" />
                    Last test failed · {item.last_tested_at?.slice(0, 16).replace('T', ' ')}
                  </span>
                )}
                {item.last_test_error && (
                  <span className="text-red-300 mt-0.5 max-w-md text-right">{item.last_test_error}</span>
                )}
                {!tested && <span>Not yet tested</span>}
                {item.updated_at && <span className="mt-0.5">Updated {item.updated_at.slice(0, 16).replace('T', ' ')}</span>}
              </div>
            </div>

            {msg.text && msg.service === item.key && (
              <div
                className={`mt-3 px-3 py-2 rounded-md text-xs ${
                  msg.type === 'ok'
                    ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                    : 'bg-red-500/10 text-red-300 border border-red-500/30'
                }`}
                data-testid={`credentials-message-${item.key}`}
              >
                {msg.text}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default CredentialsManager;
